import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChartLineUp, Warning, TrayArrowDown } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { CCNav, PeriodBar, usePeriods, fmtAmt } from "../components/cc/common";
import { CCGuide } from "../components/cc/GuideBanner";
import { Button } from "../components/ui/button";

function KPI({ label, value, sub, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xl font-semibold mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function CommissionOverviewPage() {
  const { periods, selected, setSelected, reload } = usePeriods();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!selected) return;
    api.get(`/commission/overview?period_key=${selected}`).then((r) => setData(r.data)).catch(() => {});
  }, [selected]);

  const att = data?.attention;
  const attentionTotal = att ? att.blocked + att.failed + att.unresolved_rows : 0;

  return (
    <div data-testid="commission-overview-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <ChartLineUp size={26} weight="duotone" className="text-indigo-600" /> Commission Center
          </h1>
          <p className="text-sm text-slate-500 mt-1">Carrier statement reconciliation & producer commissions — every dollar traces to source.</p>
        </div>
        <PeriodBar periods={periods} selected={selected} setSelected={setSelected}
          onCreated={async (k) => { await reload(); setSelected(k); }} />
      </div>
      <CCNav />

      <CCGuide periodKey={selected} full />

      {!periods.length ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          <p className="font-medium text-slate-700">No commission periods yet</p>
          <p className="text-sm mt-1">Open a monthly period above, then upload carrier statements and your EZLynx book of business on the Imports tab.</p>
        </div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <KPI label="Carrier positives" value={fmtAmt(data.totals?.positives)} sub="validated statements" testId="cc-kpi-positives" />
            <KPI label="Carrier negatives" value={fmtAmt(data.totals?.negatives)} sub="chargebacks & reversals" testId="cc-kpi-negatives" />
            <KPI label="Net agency commission" value={fmtAmt(data.totals?.net)} sub="staging — pre-ledger" testId="cc-kpi-net" />
            <KPI label="Statements validated" value={`${data.statements?.validated ?? 0} / ${data.statements?.total ?? 0}`}
              sub={`EZLynx snapshots: ${data.ezlynx?.validated ?? 0}/${data.ezlynx?.snapshots ?? 0}`} testId="cc-kpi-statements" />
          </div>

          {attentionTotal > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-6 flex items-center gap-3 text-sm text-amber-900" data-testid="cc-attention-banner">
              <Warning size={20} weight="duotone" />
              <span>
                <strong>Needs attention:</strong> {att.blocked > 0 && `${att.blocked} blocked`}{att.blocked > 0 && (att.failed > 0 || att.unresolved_rows > 0) && ", "}
                {att.failed > 0 && `${att.failed} failed import${att.failed > 1 ? "s" : ""}`}{att.failed > 0 && att.unresolved_rows > 0 && ", "}
                {att.unresolved_rows > 0 && `${att.unresolved_rows} unresolved row${att.unresolved_rows > 1 ? "s" : ""}`} — resolve before this period can be approved.
              </span>
              <Link to="/commissions/imports" className="ml-auto"><Button size="sm" variant="outline">Review imports</Button></Link>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-medium text-sm">Carrier-by-carrier status — {selected}</h2>
              <Link to="/commissions/imports"><Button size="sm" variant="outline" data-testid="cc-goto-imports"><TrayArrowDown size={15} className="mr-1" /> Imports</Button></Link>
            </div>
            {data.carriers.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No carrier statements uploaded for this period yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cc-carrier-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                      <th className="px-4 py-2">Carrier</th>
                      <th className="px-4 py-2">Statements</th>
                      <th className="px-4 py-2">Validated</th>
                      <th className="px-4 py-2 text-right">Extracted</th>
                      <th className="px-4 py-2 text-right">Statement total</th>
                      <th className="px-4 py-2 text-right">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.carriers.map((c) => (
                      <tr key={c.carrier_id} className="border-b border-slate-50">
                        <td className="px-4 py-2.5 font-medium">{c.carrier_name}</td>
                        <td className="px-4 py-2.5">{c.statements}</td>
                        <td className="px-4 py-2.5">
                          <span className={c.validated === c.statements ? "text-emerald-700" : "text-amber-700"}>
                            {c.validated}/{c.statements}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">{fmtAmt(c.extracted_total)}</td>
                        <td className="px-4 py-2.5 text-right">{c.source_total === null ? <span className="text-amber-600 text-xs">missing</span> : fmtAmt(c.source_total)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {c.discrepancy === null ? <span className="text-slate-400">—</span> :
                            Number(c.discrepancy) === 0 ? <span className="text-emerald-700">$0.00</span> :
                              <span className="text-red-600 font-medium">{fmtAmt(c.discrepancy)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-3">{data.totals?.note}</p>
        </>
      )}
    </div>
  );
}
